import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Anchor, Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';

const BACKGROUND_IMAGE = 'https://images.pexels.com/photos/3808621/pexels-photo-3808621.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920';

const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error.response) {
        toast.error(`Server error (${error.response.status}). Please try again.`);
      } else if (error.request) {
        toast.error('Unable to reach the server. Please check your internet connection and try again.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${BACKGROUND_IMAGE})` }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-[2px]" />
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Seafarer Logbook" className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Seafarer Logbook</h1>
          <p className="text-slate-300 mt-1">Your trips, earnings & tax — all in one place</p>
        </div>

        <Card className="bg-slate-800/90 border-slate-700 backdrop-blur-sm shadow-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg sm:text-xl">Sign In</CardTitle>
            <CardDescription className="text-slate-400 text-sm">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-11"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-slate-300 text-sm">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-amber-400 hover:text-amber-300"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-11"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="toggle-password-visibility"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-amber-600 hover:bg-amber-700 h-11 text-base font-medium shadow-lg shadow-amber-600/20"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
            <p className="text-center text-slate-400 text-sm mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-amber-400 hover:text-amber-300 font-medium">
                Create one
              </Link>
            </p>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6">
          Track your qualifying days for UK tax relief
        </p>
        <p className="text-center text-slate-600 text-xs mt-2">
          <Link to="/accountant/login" className="hover:text-slate-400 transition-colors">
            Accountant Portal
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
